import { defineStore } from "pinia";
import { store } from "@/stores/global.js";
import { generateCacheKey, clearCache } from "@/composables/useCache";
import { useNumberFormat } from "@/composables/useNumberFormat";
import { doFetch } from "@/composables/doFetch";

export const useQuotesStore = defineStore("quotes", {
    state: () => ({
        token: null,
        quotes: {},
        isLoading: false,
        lastFetchTime: {},
        fetchInProgress: {},
        cacheTimeMs: 60000, // 1 minute cache
    }),

    getters: {
        /**
         * Get all available quotes
         */
        allQuotes(state) {
            return state.quotes;
        },
        /**
         * Format a specific quote for display
         */ getQuote:
            state =>
            (symbol, extraHours = true, locale = "en-US", customLabel = null) => {
                const cacheKey = generateCacheKey(symbol, extraHours);
                const quote = state.quotes[cacheKey];
                if (!quote) return null;

                const { formatPrice, formatPercent, formatLargeNumber } = useNumberFormat(locale);
                const labelOverrides = customLabel
                    ? { [symbol.trim().toUpperCase()]: customLabel }
                    : {};
                return formatQuoteData(
                    quote,
                    {
                        formatPrice: value => formatPrice(value, locale),
                        formatPercent: value => formatPercent(value, locale),
                        formatLargeNumber: value => formatLargeNumber(value, locale),
                    },
                    labelOverrides
                );
            },
        /**
         * Get all formatted quotes for a list of symbols
         */
        getQuotesBySymbols:
            state =>
            (symbols, extraHours = true, locale = "en-US", customLabels = null) => {
                if (!symbols) return [];

                // Handle both string format and structured format
                let symbolsArray,
                    labelOverrides = {};

                if (Array.isArray(symbols)) {
                    // Check if it's structured format [{symbol: "MSFT", label: "Custom"}, ...]
                    if (symbols.length > 0 && typeof symbols[0] === "object" && symbols[0].symbol) {
                        symbolsArray = symbols.map(item => item.symbol.trim().toUpperCase());
                        symbols.forEach(item => {
                            if (item.label) {
                                labelOverrides[item.symbol.trim().toUpperCase()] = item.label;
                            }
                        });
                    } else {
                        // Regular array of symbols
                        symbolsArray = symbols.map(s => s.trim().toUpperCase());
                    }
                } else {
                    // String format "MSFT,AAPL,GOOGL"
                    symbolsArray = symbols.split(",").map(s => s.trim().toUpperCase());
                }

                // Apply customLabels parameter if provided (takes precedence)
                if (customLabels) {
                    Object.assign(labelOverrides, customLabels);
                }

                const { formatPrice, formatPercent, formatLargeNumber } = useNumberFormat(locale);

                return symbolsArray
                    .map(symbol => {
                        const cacheKey = generateCacheKey(symbol, extraHours);
                        return state.quotes[cacheKey];
                    })
                    .filter(Boolean)
                    .map(quote =>
                        formatQuoteData(
                            quote,
                            {
                                formatPrice: value => formatPrice(value, locale),
                                formatPercent: value => formatPercent(value, locale),
                                formatLargeNumber: value => formatLargeNumber(value, locale),
                            },
                            labelOverrides
                        )
                    );
            },
    },
    actions: {
        /**
         * Direct API call to fetch quotes data
         * @param {Object} params - API parameters
         * @param {string} token - Authentication token
         * @returns {Promise<Object>} - API response
         */
        async callQuotesAPI(params, token) {
            const url = new URL(`https://${store.env}.quotemedia.com/v3/quotes`);

            url.searchParams.append("token", token);
            url.searchParams.append("webmaster_id", store.wmid);
            if (store.sid) url.searchParams.append("sid", store.sid);
            url.searchParams.append("module", "datatool");

            for (const key in params) {
                url.searchParams.append(key, params[key]);
            }

            return await doFetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });
        },

        /**
         * Fetch quotes for the provided symbols
         * @param {string|string[]} symbols - Symbol or array of symbols to fetch
         * @param {string} token - Authentication token for the API call
         * @param {boolean} extraHours - Whether to include extra hours data
         * @param {boolean} force - Force refresh even if cached
         * @returns {Promise<Object>} - Object containing quotes data
         */
        async fetch(symbols, token, extraHours = false, force = false) {
            if (!symbols) return {};
            if (!token) throw new Error("Token is required for API calls");

            const symbolsArray = Array.isArray(symbols)
                ? symbols.map(s => s.trim().toUpperCase())
                : symbols.split(",").map(s => s.trim().toUpperCase());

            const requestCacheKey = generateCacheKey(symbolsArray, extraHours);

            if (this.fetchInProgress[requestCacheKey]) {
                return this.fetchInProgress[requestCacheKey];
            }

            const now = Date.now();
            const symbolsToFetch = force
                ? symbolsArray
                : symbolsArray.filter(symbol => {
                      const cacheKey = generateCacheKey(symbol, extraHours);
                      return (
                          !this.quotes[cacheKey] ||
                          !this.lastFetchTime[cacheKey] ||
                          now - this.lastFetchTime[cacheKey] > this.cacheTimeMs
                      );
                  });

            if (symbolsToFetch.length === 0) {
                return symbolsArray.reduce((result, symbol) => {
                    const cacheKey = generateCacheKey(symbol, extraHours);
                    if (this.quotes[cacheKey]) {
                        result[symbol] = this.quotes[cacheKey];
                    }
                    return result;
                }, {});
            }

            const fetchPromise = (async () => {
                try {
                    this.isLoading = true;

                    const params = {
                        symbols: symbolsToFetch.join(","),
                        afterhours: extraHours,
                        premarket: extraHours,
                        marketstatus: extraHours,
                    };

                    let response = await this.callQuotesAPI(params, token);

                    // Handle error response from doFetch
                    if (response.error) {
                        throw new Error(`API call failed: ${response.error}`);
                    }

                    let quotesArray = [];
                    if (response.results && response.results.quotes) {
                        quotesArray = response.results.quotes;
                    } else if (Array.isArray(response)) {
                        quotesArray = response;
                    }

                    const processedQuotes = {};
                    quotesArray.forEach(quote => {
                        const symbol = quote.symbolInformation?.symbolString;
                        if (symbol) {
                            const cacheKey = generateCacheKey(symbol, extraHours);
                            processedQuotes[symbol] = quote;
                            this.quotes[cacheKey] = quote;
                            this.lastFetchTime[cacheKey] = now;
                        }
                    });

                    return symbolsArray.reduce((result, symbol) => {
                        const cacheKey = generateCacheKey(symbol, extraHours);
                        if (this.quotes[cacheKey]) {
                            result[symbol] = this.quotes[cacheKey];
                        }
                        return result;
                    }, {});
                } finally {
                    this.isLoading = false;
                    delete this.fetchInProgress[requestCacheKey];
                }
            })();

            this.fetchInProgress[requestCacheKey] = fetchPromise;
            return fetchPromise;
        },

        /**
         * Clear cached data for specific symbols or all symbols
         * @param {string|string[]|null} symbols - Symbols to clear or null for all
         * @param {boolean|null} extraHours - Extra hours flag to clear or null for all
         */
        clearCache(symbols = null, extraHours = null) {
            const filters = {};
            if (symbols !== null) {
                filters[0] = Array.isArray(symbols) ? symbols : symbols.split(",");
            }
            if (extraHours !== null) {
                filters[1] = String(extraHours);
            }

            clearCache(
                {
                    dataCache: this.quotes,
                    timeCache: this.lastFetchTime,
                },
                {
                    separator: "|",
                    filters: Object.keys(filters).length > 0 ? filters : null,
                }
            );
        },

        /**
         * Get symbol details from the quotes store
         * @param {string} symbol - Symbol to get details for
         * @returns {Object|null} - Symbol details or null if not found
         */
        getSymbolDetails(symbol) {
            if (!symbol) return null;

            const normalizedSymbol = symbol.trim().toUpperCase();

            const cacheKey = generateCacheKey(normalizedSymbol, true);
            let quote = this.quotes[cacheKey];

            if (!quote) {
                const regularCacheKey = generateCacheKey(normalizedSymbol, false);
                quote = this.quotes[regularCacheKey];
            }

            if (quote) {
                return {
                    symbol: normalizedSymbol,
                    longName: quote.symbolInformation?.longName || normalizedSymbol,
                    shortName: quote.symbolInformation?.shortName || normalizedSymbol,
                    instrumentType: quote.symbolInformation?.instrumentType || "equity",
                };
            }

            return null;
        },
    },
});

/**
 * Helper function to format quote data
 * @param {Object} quote - Raw quote object
 * @param {Object} formatters - Formatting functions
 * @param {Object} labelOverrides - Custom label overrides by symbol
 * @returns {Object} - Formatted quote data
 */
function formatQuoteData(quote, formatters, labelOverrides = {}) {
    if (!quote) return null;

    const { formatPrice, formatPercent, formatLargeNumber } = formatters;

    let priceData = quote.priceData;

    let regular = {
        last: null,
        change: null,
        changePercent: null,
        changeDirection: "neutral",
        changeSign: "",
    };

    let premarket = null;
    let afterhours = null;

    if (priceData?.last !== undefined) {
        regular.last = Number(priceData.last);

        if (priceData?.change !== undefined) {
            regular.change = Number(priceData.change);
        }

        if (priceData?.changePercent !== undefined) {
            regular.changePercent = Number(priceData.changePercent);
        }

        regular.changeDirection = regular.change >= 0 ? "positive" : "negative";
        regular.changeSign = regular.change >= 0 ? "+" : "";
    }

    if (priceData?.premarket?.last !== undefined) {
        premarket = {
            last: Number(priceData.premarket.last),
            change:
                priceData.premarket?.change !== undefined
                    ? Number(priceData.premarket.change)
                    : null,
            changePercent:
                priceData.premarket?.changePercent !== undefined
                    ? Number(priceData.premarket.changePercent)
                    : null,
        };

        premarket.changeDirection = premarket.change >= 0 ? "positive" : "negative";
        premarket.changeSign = premarket.change >= 0 ? "+" : "";
    }

    if (priceData?.afterhours?.last !== undefined) {
        afterhours = {
            last: Number(priceData.afterhours.last),
            change:
                priceData.afterhours?.change !== undefined
                    ? Number(priceData.afterhours.change)
                    : null,
            changePercent:
                priceData.afterhours?.changePercent !== undefined
                    ? Number(priceData.afterhours.changePercent)
                    : null,
        };

        afterhours.changeDirection = afterhours.change >= 0 ? "positive" : "negative";
        afterhours.changeSign = afterhours.change >= 0 ? "+" : "";
    }

    let tradingSession = determineTradingSession(quote.symbolInformation, quote);

    let dataSource = "regular";
    let currentPrice = regular.last;

    if (afterhours && afterhours.last !== null && tradingSession === "afterhours") {
        dataSource = "afterhours";
        currentPrice = afterhours.last;
    } else if (premarket && premarket.last !== null && tradingSession === "premarket") {
        dataSource = "premarket";
        currentPrice = premarket.last;
    }
    return {
        symbol: quote.symbolInformation?.symbolString || "",
        shortName: quote.symbolInformation?.shortName || "",
        longName:
            labelOverrides[quote.symbolInformation?.symbolString] ||
            quote.symbolInformation?.longName ||
            "",
        last: formatPrice(regular.last),
        change: formatPrice(regular.change),
        changePercent: formatPercent(regular.changePercent),
        open: formatPrice(priceData?.open),
        high: formatPrice(priceData?.high),
        low: formatPrice(priceData?.low),
        changeDirection: regular.changeDirection,
        changeSign: regular.changeSign,
        premarket: premarket
            ? {
                  last: formatPrice(premarket.last),
                  change: formatPrice(premarket.change),
                  changePercent: formatPercent(premarket.changePercent),
                  changeDirection: premarket.changeDirection,
                  changeSign: premarket.changeSign,
              }
            : null,
        afterhours: afterhours
            ? {
                  last: formatPrice(afterhours.last),
                  change: formatPrice(afterhours.change),
                  changePercent: formatPercent(afterhours.changePercent),
                  changeDirection: afterhours.changeDirection,
                  changeSign: afterhours.changeSign,
              }
            : null,
        tradingSession,
        volume: formatLargeNumber(priceData?.shareVolume || 0),
        marketCap: formatLargeNumber(quote.fundamental?.marketCap || 0),
        lastTradeDateTime: priceData?.lastTradeDateTime || "",
        dataSource,
        rawData: quote,
    };
}

/**
 * Determines the current trading session based on market open/close times
 * @param {Object} symbolInfo - Symbol information containing open/close times
 * @returns {String} - Current trading session: 'premarket', 'regular', 'afterhours'
 */
function determineTradingSession(symbolInfo, quote) {
    const isRegularHours = symbolInfo?.exchange?.isCurrentlyOpen == "true";
    const isExtendedHours = symbolInfo?.exchange?.isOpen == "true";

    // If it's not within extended hours, exchange is closed
    if (!isExtendedHours) {
        return "closed";
    }

    // If the symbol information is not available, or isRegularHours=true, we assume it's regular hours
    if (!symbolInfo || isRegularHours) return "regular";

    const now = Date.now() / 1000;

    let openTime = symbolInfo?.exchange?.openTime ? Number(symbolInfo.exchange.openTime) : null;
    let closeTime = symbolInfo?.exchange?.closeTime ? Number(symbolInfo.exchange.closeTime) : null;

    // For extended hours detection, we rely on if the quote has premarket or afterhours data,
    // and the current time in relation to the open/close times of the exchange
    // otherwise we assume it's closed or during regular hours
    if (quote.priceData?.afterhours?.last && now > closeTime) {
        return "afterhours";
    } else if (quote.priceData?.premarket?.last && now < openTime) {
        return "premarket";
    } else {
        return "regular";
    }
}
